import { z } from 'zod'
import { OptionSchema, NodeKeySchema } from './common'
import { PageDesignSchema } from './blocks'
import { ThemeSchema } from './theme'
import { ExprAstSchema } from './expr'

export const NODE_TYPES = [
  'inputText',
  'dropdown',
  'buttonChoice',
  'multiSelect',
  'form',
  'decision',
  'camera',
  'end',
] as const
export type NodeType = (typeof NODE_TYPES)[number]

export const NODE_TYPE_LABELS: Record<NodeType, string> = {
  inputText: 'พิมพ์ข้อความ',
  dropdown: 'เลือกจากรายการ',
  buttonChoice: 'เลือก 1 ข้อ (ปุ่ม)',
  multiSelect: 'เลือกได้หลายข้อ',
  form: 'ฟอร์มหลายช่อง',
  decision: 'เงื่อนไข (ไม่ถาม)',
  camera: 'ถ่ายภาพ',
  end: 'จบแบบสอบถาม',
}

const PositionSchema = z.strictObject({ x: z.number(), y: z.number() })

const nodeBase = {
  id: z.string().min(1),
  /** รหัสอ้างอิงข้ามขั้นตอน (Q1, Q2, ...) — กำหนดอัตโนมัติและไม่เปลี่ยนอีก */
  key: NodeKeySchema,
  title: z.string().default(''),
  description: z.string().default(''),
  position: PositionSchema,
  required: z.boolean().default(true),
  /** หน้าที่ออกแบบเอง — null = ใช้ฟอร์มมาตรฐานตามชนิดของโหนด */
  page: PageDesignSchema.nullable().default(null),
  /** ปิดใช้งานขั้นตอนนี้ชั่วคราว — เอ็นจินจะข้ามไปเองโดยไม่ถามผู้ตอบ (ไม่ใช่การลบ) */
  disabled: z.boolean().default(false),
}

export const FlowNodeSchema = z.discriminatedUnion('type', [
  z.strictObject({
    ...nodeBase,
    type: z.literal('inputText'),
    placeholder: z.string().default(''),
    multiline: z.boolean().default(false),
    inputMode: z.enum(['text', 'number', 'tel']).default('text'),
  }),
  z.strictObject({
    ...nodeBase,
    type: z.literal('dropdown'),
    options: z.array(OptionSchema).min(1, 'ต้องมีอย่างน้อย 1 ตัวเลือก'),
  }),
  z.strictObject({
    ...nodeBase,
    type: z.literal('buttonChoice'),
    options: z.array(OptionSchema).min(1, 'ต้องมีอย่างน้อย 1 ตัวเลือก'),
  }),
  z.strictObject({
    ...nodeBase,
    type: z.literal('multiSelect'),
    options: z.array(OptionSchema).min(1, 'ต้องมีอย่างน้อย 1 ตัวเลือก'),
    maxSelect: z.number().int().positive().nullable().default(null),
  }),
  /** ฟอร์มหลายช่อง — ฟิลด์ทั้งหมดมาจากหน้าที่ออกแบบ (page) */
  z.strictObject({ ...nodeBase, type: z.literal('form') }),
  /** โหนดตัดสินใจ ไม่ถามอะไร แค่แตกเส้นทางจากคำตอบก่อนหน้า */
  z.strictObject({ ...nodeBase, type: z.literal('decision') }),
  z.strictObject({
    ...nodeBase,
    type: z.literal('camera'),
    maxPhotos: z.number().int().min(1).max(10).default(1),
  }),
  z.strictObject({
    ...nodeBase,
    type: z.literal('end'),
    message: z.string().default('ขอบคุณสำหรับข้อมูลครับ/ค่ะ'),
  }),
])

export type FlowNode = z.infer<typeof FlowNodeSchema>
export type FlowNodeOf<T extends NodeType> = Extract<FlowNode, { type: T }>

/** โหนดที่มีชุดตัวเลือกในตัวเอง */
export type ChoiceNode = Extract<FlowNode, { options: unknown }>
export function isChoiceNode(node: FlowNode): node is ChoiceNode {
  return node.type === 'dropdown' || node.type === 'buttonChoice' || node.type === 'multiSelect'
}

/**
 * เงื่อนไขของเส้นเชื่อม — เป็นโครงสร้างข้อมูล ไม่ใช่ข้อความที่ต้อง parse ตอนรัน
 * chip picker ในหน้า builder ผลิตได้เฉพาะชนิดที่มีโครงสร้าง ส่วน `expr`
 * เป็นทางออกสำหรับเงื่อนไขซับซ้อน (parse เป็น AST ตั้งแต่ตอนแก้ไข)
 */
export const EdgeConditionSchema = z.discriminatedUnion('kind', [
  /** ไปต่อเสมอ (ใช้กับโหนดที่มีทางออกเดียว) */
  z.strictObject({ kind: z.literal('always') }),
  /** ทุกกรณีอื่น — ถูกประเมินเป็นลำดับสุดท้ายเสมอ */
  z.strictObject({ kind: z.literal('fallback') }),
  /** คำตอบแบบเลือก 1 ข้อ ตรงกับตัวเลือกนี้ */
  z.strictObject({
    kind: z.literal('optionEquals'),
    nodeKey: z.string().min(1),
    fieldName: z.string().min(1),
    optionId: z.string().min(1),
  }),
  /** คำตอบแบบเลือกหลายข้อ มีตัวเลือกนี้อยู่ */
  z.strictObject({
    kind: z.literal('optionIncludes'),
    nodeKey: z.string().min(1),
    fieldName: z.string().min(1),
    optionId: z.string().min(1),
  }),
  /** ฟิลด์นี้ถูกตอบ (ใช้แยกเส้นทางของโหนดฟอร์มที่มีหลายฟิลด์) */
  z.strictObject({
    kind: z.literal('fieldAnswered'),
    nodeKey: z.string().min(1),
    fieldName: z.string().min(1),
  }),
  z.strictObject({
    kind: z.literal('textEquals'),
    nodeKey: z.string().min(1),
    fieldName: z.string().min(1),
    value: z.string(),
    caseInsensitive: z.boolean().default(true),
  }),
  z.strictObject({
    kind: z.literal('numberCompare'),
    nodeKey: z.string().min(1),
    fieldName: z.string().min(1),
    op: z.enum(['eq', 'ne', 'lt', 'lte', 'gt', 'gte']),
    value: z.number(),
  }),
  /** เงื่อนไขขั้นสูง: เก็บทั้งข้อความที่ผู้ใช้พิมพ์และ AST ที่ parse แล้ว */
  z.strictObject({
    kind: z.literal('expr'),
    source: z.string().min(1),
    ast: ExprAstSchema,
  }),
])

export type EdgeCondition = z.infer<typeof EdgeConditionSchema>
export type EdgeConditionKind = EdgeCondition['kind']

export const FlowEdgeSchema = z.strictObject({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  /** ลำดับการประเมิน (น้อยมาก่อน) — ทำให้ผลลัพธ์คาดเดาได้เสมอ */
  priority: z.number().int().default(0),
  condition: EdgeConditionSchema,
  /** ป้ายกำกับที่ผู้ใช้ตั้งเอง (ไม่ระบุ = สร้างจากเงื่อนไข) */
  label: z.string().default(''),
})

export type FlowEdge = z.infer<typeof FlowEdgeSchema>

export const FLOW_SCHEMA_VERSION = 1 as const

export const FlowDefinitionSchema = z.strictObject({
  schemaVersion: z.literal(FLOW_SCHEMA_VERSION).default(FLOW_SCHEMA_VERSION),
  startNodeId: z.string().min(1),
  nodes: z.array(FlowNodeSchema).min(1, 'ต้องมีอย่างน้อย 1 ขั้นตอน'),
  edges: z.array(FlowEdgeSchema).default([]),
  theme: ThemeSchema.default(() => ThemeSchema.parse({})),
})

export type FlowDefinition = z.infer<typeof FlowDefinitionSchema>
